import {
  ACTION_ARGS,
  COMPONENT_STATE_ARGS,
  CONTAINER_ARGS,
  CONTENT_ARGS,
  MENU_ARGS,
  pickArgs,
} from '../../../../../utils/spds/metadata';
import { ARRAY, STRING } from '../../../../../utils/component-validations';
import { ensureSafeComponent } from '@embroider/util';
import { fn, hash } from '@ember/helper';
import { or } from 'ember-truth-helpers';
import { pluralize } from 'ember-inflector';
import { tracked } from '@glimmer/tracking';
import FilterButton from './base.gjs';
import FlexRow from '../../../layout/flex/row.gjs';
import Super from './single-arrow-navigation.gjs';
import closeOnClickOutside from '../../../../../modifiers/close-on-click-outside';
import styles from './single-select.module.scss';
import svgJar from 'ember-svg-jar/helpers/svg-jar';

const METADATA = {
  component: 'Spds::Action::Button::Filter::SingleSelect',
  description: 'Single-select filter button with dropdown menu',
  meta: {
    ...pickArgs(COMPONENT_STATE_ARGS, ['disabled']).meta,
    ...pickArgs(CONTAINER_ARGS, ['fitContent']).meta,
    ...pickArgs(MENU_ARGS, ['menuAnchorOffset', 'menuFitContent']).meta,
    filterFor: {
      type: STRING,
      description: 'Name of what is being filtered (will be pluralized)',
    },
    icon: {
      ...CONTENT_ARGS.meta.icon,
      description: 'Icon to display in button',
    },
    onSelect: {
      ...ACTION_ARGS.meta.onClick,
      description: 'Action triggered when an option is selected',
    },
    options: {
      type: ARRAY,
      description: 'Array of option objects with label and value',
    },
    selectedOption: {
      type: STRING,
      description: 'Currently selected option value',
    },
  },
  name: 'Single Select Filter',
};

export default class SpdsActionButtonFilterSingleSelect extends Super {
  static metadata = this.setMetadata(METADATA);
  baseClass = 'spds-action-button-filter-single-select';
  componentName = 'Spds::Action::Button::Filter::SingleSelect';
  styles = styles;

  constructor() {
    super(...arguments);

    this.arguments = this.composeArguments(METADATA);
  }

  @tracked open = false;

  get filtered() {
    return this.value !== this.defaultOption;
  }

  get menuAnchor() {
    return this.menuTriggerComponent.baseElement;
  }

  get pluralizedFilterFor() {
    return pluralize(this.args.filterFor);
  }

  get selectedLabel() {
    const selected = this.navigableOptions.find(option => option.dataset.value === this.value);
    return selected?.textContent.trim() ?? `All ${this.pluralizedFilterFor}`;
  }

  handleSelect = ({ target }) => {
    this.selected = target?.dataset.value || null;
    if (this.isCurrentValue(this.selected)) {
      this.open = false;
      return;
    }

    const currentSelection = this.findOptionByValue(this.value);

    currentSelection?.setAttribute('aria-selected', 'false');

    this.value = this.selected;
    this.currentValue = this.value;
    target.setAttribute('aria-selected', 'true');
    this.args.onSelect?.(this.value);
    this.open = false;
  };

  handleOnClick = event => {
    this.args.onClick?.(event);
    this.open = !this.open;
    this.removeFocus();
  };

  isSelected = value => {
    return this.value === value ? 'true' : 'false';
  };

  <template>
    <FilterButton
      @disabled={{@disabled}}
      @fitContent={{@fitContent}}
      @filtered={{this.filtered}}
      @menuAnchorOffset={{@menuAnchorOffset}}
      @menuFitContent={{@menuFitContent}}
      @onClick={{this.handleOnClick}}
      @onSelect={{this.handleSelect}}
      @open={{this.open}}
      class={{this.classes}}
      data-test-spds-action-button-filter-single-select
      {{closeOnClickOutside this.onClose}}
      {{this.setupComponent}}
      {{this.validateArguments}}
      ...attributes
      as |FB|
    >
      <FB.Trigger
        @registerComponent={{fn this.registerComponent "menuTriggerComponent"}}
        @unregisterComponent={{fn this.unregisterComponent "menuTriggerComponent"}}
      >
        {{svgJar (or @icon "filter")}}
        <FlexRow>
          {{this.selectedLabel}}
        </FlexRow>
        {{svgJar "dropdown-caret" class="spds-action-button-filter-single-select-caret"}}
      </FB.Trigger>
      <FB.Menu
        @anchorElement={{this.menuAnchor}}
        @registerComponent={{fn this.registerComponent "menuComponent"}}
        @unregisterComponent={{fn this.unregisterComponent "menuComponent"}}
        as |menu|
      >
        <menu.item @showSelectedStyles={{false}} @value={{this.defaultOption}}>
          All
          {{this.pluralizedFilterFor}}
        </menu.item>
        {{#if @options}}
          {{#each @options as |option|}}
            <menu.item @showSelectedStyles={{false}} @value={{or option.value option.label}}>
              {{option.label}}
            </menu.item>
          {{/each}}
        {{else}}
          {{yield (hash item=(component (ensureSafeComponent menu.item) showSelectedStyles=false))}}
        {{/if}}
      </FB.Menu>
    </FilterButton>
  </template>
}

export const ACTION_BUTTON_FILTER_SINGLE_SELECT_METADATA =
  SpdsActionButtonFilterSingleSelect.metadata;
